import { useMemo, useState } from 'react';
import { categoryChips, newsItems } from '@/mocks/home';
import type { NewsItem } from '@/mocks/home';
import SectionHeading from './SectionHeading';

function MetaRow({ item }: { item: NewsItem }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-label text-foreground-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-time-line text-sm" aria-hidden="true"></i>
        </span>
        {item.time}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-eye-line text-sm" aria-hidden="true"></i>
        </span>
        {item.reads} 阅读
      </span>
    </div>
  );
}

function FeatureCard({ item }: { item: NewsItem }) {
  return (
    <article className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200 lg:col-span-2">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="h-52 w-full overflow-hidden sm:h-full sm:min-h-[240px]">
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - 黑料网站资讯汇总`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
        <div className="flex flex-col justify-center p-5 md:p-6">
          <span className="inline-flex w-fit rounded-md bg-secondary-100 px-2.5 py-1 text-[11px] font-label text-secondary-800">
            {item.category}
          </span>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
            <a href="#feed" className="transition-colors hover:text-primary-600">
              {item.title}
            </a>
          </h3>
          <p className="mt-3 text-sm leading-7 text-foreground-600">{item.excerpt}</p>
          <MetaRow item={item} />
        </div>
      </div>
    </article>
  );
}

function ImageCard({ item }: { item: NewsItem }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <div className="h-44 w-full overflow-hidden">
        <img
          src={item.image}
          alt={item.title}
          title={`${item.title} - 黑料网站资讯汇总`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <span className="inline-flex w-fit rounded-md bg-secondary-100 px-2.5 py-1 text-[11px] font-label text-secondary-800">
          {item.category}
        </span>
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950">
          <a href="#feed" className="transition-colors hover:text-primary-600">
            {item.title}
          </a>
        </h3>
        <p className="mt-3 flex-1 text-sm leading-6 text-foreground-600">{item.excerpt}</p>
        <MetaRow item={item} />
      </div>
    </article>
  );
}

function TextCard({ item }: { item: NewsItem }) {
  return (
    <article className="flex flex-col rounded-lg border border-background-200 bg-background-100/60 p-5 transition-colors hover:border-accent-300 hover:bg-background-50">
      <div className="flex items-center justify-between">
        <span className="inline-flex rounded-md bg-accent-100 px-2.5 py-1 text-[11px] font-label text-accent-900">
          {item.category}
        </span>
        <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
          <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
        </span>
      </div>
      <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950">
        <a href="#feed" className="transition-colors hover:text-primary-600">
          {item.title}
        </a>
      </h3>
      <p className="mt-3 flex-1 text-sm leading-6 text-foreground-600">{item.excerpt}</p>
      <MetaRow item={item} />
    </article>
  );
}

export default function NewsGrid() {
  const [activeCat, setActiveCat] = useState('全部');

  const list = useMemo(
    () => (activeCat === '全部' ? newsItems : newsItems.filter((item) => item.category === activeCat)),
    [activeCat],
  );

  return (
    <section id="feed" className="w-full border-b border-background-200/70 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <SectionHeading
          index="03"
          label="News Feed"
          title="爆料速览 · 大小卡混排"
          desc="站内资讯按话题分类整理，重要内容以大卡图文并重呈现，短讯则以紧凑卡片排布，图文并重方便按兴趣快速浏览。"
          extra={
            <a
              href="#ranking"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-label text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              查看全部资讯
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
              </span>
            </a>
          }
        />

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {categoryChips.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => setActiveCat(chip)}
              className={
                activeCat === chip
                  ? 'cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-xs font-label font-medium text-background-50 md:text-sm'
                  : 'cursor-pointer whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-2 text-xs font-label text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 md:text-sm'
              }
            >
              {chip}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {list.map((item) => {
            if (item.size === 'feature') return <FeatureCard key={item.id} item={item} />;
            if (item.size === 'image') return <ImageCard key={item.id} item={item} />;
            return <TextCard key={item.id} item={item} />;
          })}
        </div>

        {list.length === 0 && (
          <p className="mt-10 rounded-lg border border-dashed border-background-300 py-14 text-center text-sm font-label text-foreground-500">
            该分类暂时没有内容，试试切换其他分类。
          </p>
        )}
      </div>
    </section>
  );
}